import React, { memo, useEffect, useRef, useState } from 'react';
import { useFormikContext, useField, ErrorMessage } from 'formik';
import styled from 'styled-components';

import type { CountryData } from 'react-phone-input-2';
import {
  // eslint-disable-next-line import/no-named-default
  default as PhoneInputComponent,
} from 'react-phone-input-2';

import type { SetSharedState, SharedState } from 'src/components/AuthNew/types';
import {
  resetPhoneFormat,
  setPhoneFormat,
  setPhoneForPlaceholder,
} from 'src/components/AuthNew/utils';

import Done from '@lt/components/icons-react/Done';
import { useCountry, useTranslation } from '@lt/localization';
import { localization } from '../helpers/localization';
import { PhoneInputWrapper } from '../styles/styles';

interface InputProps {
  type: string;
  disabled?: boolean;
}

type Props = {
  setIsContinueBtnDisabled?: (state: boolean) => void;
  setSharedState?: SetSharedState;
  sharedState?: SharedState;
  hint?: string;
  name: string;
  className?: string;
  inputProps?: InputProps;
  renderAppendAddon?: (value: string) => JSX.Element | null;
};

export const PhoneInput = memo(
  ({
    setIsContinueBtnDisabled,
    setSharedState,
    sharedState,
    hint,
    name,
    className,
    inputProps,
    renderAppendAddon,
  }: Props) => {
    const { t } = useTranslation();
    const { country } = useCountry();

    const [field] = useField({ name });
    const [formattedPhone, setFormattedPhone] = useState('');

    const { onChange: onChangeFormik, value: formikValue } = field;
    const { setFieldValue, setFieldTouched, touched, errors } =
      useFormikContext();

    const [selectedCountry, setSelectedCountry] = useState(
      sharedState?.country || country || 'ru',
    );
    const [placeholder, setPlaceholder] = useState('');
    const [mask, setMask] = useState('');
    const [focus, setFocus] = useState(false);
    // стейт нужен для логики появления зеленой галочки
    const [isError, setIsError] = useState(false);
    const phoneInputRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(0);

    /**
     * костыль для нормальной работы position: sticky в некоторых iOS
     * вычисляем ширину враппера и применяем ее к списку со странами ul
     *  */
    useEffect(() => {
      if (phoneInputRef.current)
        setWidth(phoneInputRef.current.getBoundingClientRect().width);
    }, []);

    useEffect(() => {
      if (field.value) setFormattedPhone(setPhoneFormat(mask, field.value));
    }, [field.value, mask]);

    const isValidNumber = (
      currentValue: string,
      currentCountryMask: string,
    ): boolean => {
      if (
        (currentCountryMask.match(/\./g) || []).length === currentValue.length
      ) {
        if (setIsContinueBtnDisabled) setIsContinueBtnDisabled(false);
        return true;
      }
      if (setIsContinueBtnDisabled) setIsContinueBtnDisabled(true);
      return false;
    };

    const onChange = (
      val: string,
      currentCountry: CountryData,
      _,
      formattedValue: string,
    ) => {
      if (setSharedState) {
        if (currentCountry.countryCode !== selectedCountry) {
          const newCountry = currentCountry.countryCode;
          setSelectedCountry(newCountry);
          setSharedState((prev) => ({
            ...prev,
            country: newCountry,
            phone_mask: currentCountry.format,
          }));
        }
        setPlaceholder(
          setPhoneForPlaceholder(
            currentCountry.format,
            currentCountry.dialCode,
          ),
        );
      }
      const phoneFormatted = setPhoneFormat(
        currentCountry.format,
        `+${currentCountry.dialCode}${val}`,
      );
      /**
       * Используется для автозаполнения номера телефона в мобильной версии.
       * Номер телефона заполняется без +7, поэтому добавил такую проверку
       */
      if (
        phoneFormatted !== `+${currentCountry.dialCode}${val}` &&
        Math.abs(resetPhoneFormat(formikValue).length - val.length) > 1
      ) {
        onChangeFormik(phoneFormatted);
        setFieldValue(name, phoneFormatted);
      } else {
        isValidNumber(val, currentCountry.format);
        onChangeFormik(formattedValue);
        setFieldValue(name, formattedValue);
      }
    };

    const isWithoutError = (value, currentCountry) => {
      // поскольку эта функция единственная вызывается при первом рендере компонента,
      // устанавливаем в ней маску телефона страны, выбранной по дефолту и плейсхолдер
      if (mask !== currentCountry.format) {
        setMask(currentCountry.format);
      }

      if (!placeholder) {
        setPlaceholder(
          setPhoneForPlaceholder(
            currentCountry.format,
            currentCountry.countryCode,
          ),
        );

        if (setSharedState && !sharedState?.phone_mask) {
          setSharedState((prev) => ({
            ...prev,
            phone_mask: currentCountry.format,
          }));
        }
      }

      if (
        !isValidNumber(value, currentCountry.format) &&
        !focus &&
        touched[name] &&
        value !== ''
      ) {
        setIsError(true);
        // возвращаем true, чтобы не рендерить блок с error-message, встроенный в библиотечный компонент
        // за показ error-message отвечает формик
        return true;
      }
      setIsError(false);
      return true;
    };

    const allowedNumbersCount = () => {
      if (placeholder.replace(/\D/g, '').length > 15)
        return placeholder.replace(/\D/g, '').length;
      return undefined;
    };

    const priority = () => {
      // если номер начинается с 76 или 77, значит он казахский
      // в таком случае выставляем приоритет казахам
      const firstNumbers = formattedPhone.slice(0, 2);
      if (firstNumbers === '76' || firstNumbers === '77') {
        if (selectedCountry !== 'kz') setSelectedCountry('kz');
        return { kz: 0, ru: 1 };
      }
      return { kz: 1, ru: 0 };
    };

    const hasError = Boolean(errors[name] && touched[name]);
    const isCompleted =
      Boolean(!errors[name]) &&
      Boolean(touched[name]) &&
      Boolean(formattedPhone);
    const isShowSuccessIcon = !focus && isCompleted && !isError;

    return (
      <div className={className} ref={phoneInputRef}>
        <InputHeader>{t('checkout.phoneInputForm.header')}</InputHeader>
        <PhoneInputWrapper $hasError={hasError && !focus} $width={width}>
          <PhoneInputComponent
            disableCountryGuess
            enableLongNumbers={allowedNumbersCount()}
            // передаем отформатированный value
            value={formattedPhone}
            disabled={inputProps?.disabled}
            inputProps={{ name, ...inputProps, autoComplete: 'tel' }}
            enableSearch
            // хак для верного отображения выбранной на предыдущем шаге страны
            onlyCountries={inputProps?.disabled ? [selectedCountry] : []}
            country={selectedCountry}
            priority={priority()}
            searchPlaceholder={t('checkout.phoneInputForm.searchPlaceholder')}
            searchNotFound={t('checkout.phoneInputForm.searchNotFound')}
            disableSearchIcon
            autocompleteSearch
            localization={localization}
            onChange={onChange}
            placeholder={placeholder}
            onBlur={() => {
              setFocus(false);
              setFieldTouched(name);
            }}
            onFocus={() => {
              setFocus(true);
            }}
            isValid={isWithoutError}
          />
          {isShowSuccessIcon && (
            <DoneStyledWrapper>
              <DoneStyled />
            </DoneStyledWrapper>
          )}
        </PhoneInputWrapper>
        {hasError && (
          <StyledInputError>
            <ErrorMessage name={name} />
            {renderAppendAddon && renderAppendAddon(formattedPhone)}
          </StyledInputError>
        )}

        {hint && !hasError && <InputHint>{hint}</InputHint>}
      </div>
    );
  },
);

const InputHeader = styled.div`
  font-weight: 400;
  font-size: 9px;
  line-height: 16px;
  color: #4d4d4d;
  margin-bottom: 4px;
  text-transform: uppercase;
  text-align: left;
  margin-top: 4px;
`;

const InputHint = styled.div`
  margin-top: 4px;
  color: #919499;
  text-align: left;
  max-width: fit-content;
`;

const DoneStyledWrapper = styled.div`
  width: 15px;
  display: inline-block;
  position: absolute;
  top: 50%;
  right: 0;
  transform: translate(-7px, -50%);
`;
const DoneStyled = styled(Done)`
  path {
    stroke: #459e18;
  }
`;

const StyledInputError = styled.div`
  text-align: left;
  margin-top: 4px;
  color: #d0021b;
  max-width: fit-content;
`;
